import { useEffect, useRef, useState } from "react";

import { MODES, type ThemeMode } from "../theme/mode";

interface Props {
  current: string;
  onChange: (mode: ThemeMode) => void;
}

/** Light / dark / follow-system, as a button that opens a vertical dropdown. */
export function ModeToggle({ current, onChange }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape, which is what people expect of a dropdown and
  // what stops it being left hanging open behind the rest of the page.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent): void {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const active = MODES.find((mode) => mode.id === current) ?? MODES[2];

  return (
    <div className="mode-picker" ref={container}>
      {/* Icon only — the label lives in the menu, and on the accessible name here. */}
      <button
        type="button"
        className="mode-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Colour mode: ${active.label}`}
        title={`Colour mode: ${active.label}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">{active.icon}</span>
      </button>

      {open && (
        <ul className="mode-menu card" role="menu">
          {MODES.map((mode) => (
            <li key={mode.id}>
              {/* Icon only, like the trigger. The name is carried by aria-label and the
                  tooltip; the highlight shows which one is active. */}
              <button
                type="button"
                role="menuitemradio"
                aria-checked={mode.id === current}
                aria-label={mode.label}
                title={mode.label}
                className={mode.id === current ? "mode-option active" : "mode-option"}
                onClick={() => {
                  setOpen(false);
                  onChange(mode.id);
                }}
              >
                <span aria-hidden="true">{mode.icon}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
